import React, { useState } from 'react';
import { Cake, Heart, Star, ChefHat } from 'lucide-react';
import { mockData, mockFormHandlers } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

export const CustomCakes = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { toast } = useToast();

  const categories = ['All', 'Wedding Cakes', 'Baby Shower Cakes', 'Birthday Cakes', 'Special Occasions'];

  const filteredCakes = selectedCategory === 'All' 
    ? mockData.customCakes 
    : mockData.customCakes.filter(cake => cake.category === selectedCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <Cake size={64} color="var(--color-gold)" style={{ margin: '0 auto 32px auto' }} />
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>Custom Cake Designs</h1>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto' 
            }}>
              Every celebration deserves a masterpiece. Our custom cakes are works of art, 
              crafted with precision, passion, and the finest ingredients to make your special moments unforgettable.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section style={{ background: 'var(--bg-secondary)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="heading-1" style={{ marginBottom: '32px' }}>Our Cake Portfolio</h2>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flexWrap: 'wrap', 
              gap: '16px' 
            }}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    padding: '12px 24px',
                    background: selectedCategory === category ? 'var(--color-gold)' : 'transparent',
                    color: selectedCategory === category ? 'white' : 'var(--text-primary)',
                    border: '1px solid var(--color-gold)',
                    borderRadius: '0px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.background = 'var(--color-gold)';
                      e.target.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.background = 'transparent';
                      e.target.style.color = 'var(--text-primary)';
                    }
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Cake Gallery */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '32px' 
          }}>
            {filteredCakes.map((cake) => (
              <Card key={cake.id} className="hover-lift" style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'relative' }}>
                  <img
                    src={cake.url}
                    alt={cake.alt}
                    style={{
                      width: '100%',
                      height: '300px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'var(--color-gold)',
                    color: 'white',
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {cake.category}
                  </div>
                </div>
                
                <CardContent style={{ padding: '24px' }}>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>{cake.title}</h3>
                  <p className="body-small" style={{ 
                    color: 'var(--text-secondary)',
                    marginBottom: '20px'
                  }}>
                    {cake.description}
                  </p>
                  <Button className="btn-gold" style={{ width: '100%' }}>
                    Inquire About This Design
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Food Allergy Information */}
      <section style={{ 
        background: 'var(--color-fence-green)', 
        color: 'white', 
        padding: '60px 0',
        borderTop: '4px solid var(--color-gold)',
        borderBottom: '4px solid var(--color-gold)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'var(--color-gold)',
              borderRadius: '0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <Heart size={32} color="white" />
            </div>
            
            <h2 className="heading-1" style={{ marginBottom: '24px', color: 'white' }}>
              Your Safety is Our Priority
            </h2>
            
            <p className="body-large" style={{ 
              marginBottom: '32px',
              color: 'rgba(255, 255, 255, 0.95)',
              maxWidth: '700px',
              margin: '0 auto 32px auto',
              lineHeight: '1.7'
            }}>
              <strong>Please inform us of any food allergies or dietary restrictions</strong> when placing your order. 
              Our kitchen works with various ingredients including nuts, dairy, eggs, gluten, and tropical fruits. 
              We take all necessary precautions to prevent cross-contamination, but we want to ensure your complete safety and enjoyment.
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '20px',
              maxWidth: '800px',
              margin: '0 auto 40px auto',
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <p className="body-regular" style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>
                  Common Allergens We Work With:
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
                {['Nuts', 'Dairy', 'Eggs', 'Gluten', 'Soy', 'Coconut'].map((allergen) => (
                  <span key={allergen} style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '0px',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    {allergen}
                  </span>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Discuss My Allergies
              </Button>
              <Button 
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '0px',
                  padding: '19px 23px',
                  minWidth: '210px',
                  height: '60px'
                }}
              >
                View Allergy-Free Options
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '80px' }}>
            Our Design Process
          </h2>
          
          <div className="grid-three-column">
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--color-gold)',
                borderRadius: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto'
              }}>
                <Heart size={32} color="white" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>1. Consultation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                We discuss your vision, occasion, guest count, dietary needs, and design preferences
              </p>
            </div>
            
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--color-fence-green)',
                borderRadius: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto'
              }}>
                <Star size={32} color="white" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>2. Design & Quote</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                We create a custom design concept and provide detailed pricing for your approval
              </p>
            </div>
            
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--color-gold)',
                borderRadius: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto'
              }}>
                <Cake size={32} color="white" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>3. Creation & Delivery</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                We craft your masterpiece and deliver it fresh for your special celebration
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding-small">
        <div className="container">
          <div style={{ 
            textAlign: 'center',
            background: 'var(--bg-primary)',
            padding: '80px 40px',
            borderRadius: '0px',
            border: '1px solid var(--border-light)'
          }}>
            <Cake size={64} color="var(--color-gold)" style={{ margin: '0 auto 32px auto' }} />
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
              Ready to Create Your Dream Cake?
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              Let's bring your vision to life with a custom cake that's as unique and special as your celebration. 
              Every detail is crafted with love and artistic precision.
            </p>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Start Your Custom Design
              </Button>
              <Button className="btn-secondary">
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};